import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createUseStyles } from 'react-jss';
import { S, A, DARK } from './shellTokens';

export type PeekKind = 'widget' | 'custom' | 'preset';
/** Shown in place of a preview. */
export type Blank = { icon: string; title: string; text: string };
export type PeekItem = {
  key: string; kind: PeekKind; id: string; title: string; meta: string;
  /** Nothing to load: there's nothing to see until it's added (the AI widget is empty until something is pasted). */
  blank?: Blank;
};
type Device = 'desktop' | 'mobile';

const DEVICES: Record<Device, { label: string; width: number; height: number }> = {
  desktop: { label: 'Desktop', width: 1280, height: 900 },
  mobile: { label: 'Mobile', width: 390, height: 844 }
};
const WIDTH = 600;
const BLANK_HEIGHT = 200;
// A section that loaded and is still 0 tall: an empty container, one that works behind the scenes, one drawn
// over the section above it.
const EMPTY: Blank = {
  icon: 'visibility_off',
  title: 'Nothing to show on its own',
  text: 'With its default settings this widget draws nothing here. Add it, then set it up.'
};
const failed = (message: string): Blank => ({ icon: 'error_outline', title: 'No preview', text: message });
// Middle of the header (title and meta): what lines up with the row.
const HEADER_MID = 30;
const PAD = 16;

export const previewSrc = (pageId: string, item: Pick<PeekItem, 'kind' | 'id'>) =>
  `/admin/section-preview?page=${encodeURIComponent(pageId)}&kind=${item.kind}&id=${encodeURIComponent(item.id)}`;

type Props = {
  pageId: string;
  item: PeekItem;
  /** The Insert panel: the peek sits beside it, and clicks in it don't close the peek. */
  anchor: HTMLElement | null;
  placement: string;
  onAdd: () => void;
  onClose: () => void;
  /** Loading ahead, out of sight (the pointer is on the item's preview button): shown at once when opened. */
  hidden?: boolean;
};

// empty: loaded with nothing to show. error: the preview couldn't be made (the reason).
type Frame = { src: string; height: number | null; device: Device; empty?: boolean; error?: string };

const useStyles = createUseStyles({
  '@keyframes peekIn': { from: { opacity: 0, transform: 'translateX(-8px)' }, to: { opacity: 1, transform: 'none' } },
  '@keyframes titleFlash': { from: { background: 'rgba(154, 102, 51, 0.22)' }, to: { background: 'rgba(154, 102, 51, 0)' } },
  sheet: {
    position: 'fixed',
    zIndex: 1050,
    width: WIDTH,
    display: 'flex',
    flexDirection: 'column',
    background: S.chrome,
    border: `1px solid ${S.line}`,
    borderRadius: 10,
    boxShadow: '0 18px 48px rgba(23, 26, 32, 0.22), 0 2px 6px rgba(23, 26, 32, 0.08)',
    fontFamily: S.ui,
    color: S.fg,
    animation: '$peekIn 140ms ease-out',
    transition: 'top 180ms ease',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
    [DARK]: { boxShadow: A.pop }
  },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px 10px 16px', borderBottom: `1px solid ${S.line}` },
  titles: { minWidth: 0, marginRight: 'auto' },
  title: { fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 -4px', padding: '0 4px', borderRadius: 4 },
  // When the preview switches to another item, its name flashes so the change is noticed.
  flash: { animation: '$titleFlash 700ms ease-out', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } },
  // Points at the row being previewed.
  notch: {
    position: 'absolute',
    left: -7,
    width: 12,
    height: 12,
    marginTop: -6,
    transform: 'rotate(45deg)',
    background: S.chrome,
    borderLeft: `1px solid ${S.line}`,
    borderBottom: `1px solid ${S.line}`,
    transition: 'top 160ms ease',
    pointerEvents: 'none',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
  },
  meta: { fontFamily: S.mono, fontSize: 11, color: S.muted, marginTop: 1 },
  segment: { display: 'flex', padding: 2, gap: 2, borderRadius: 7, background: S.line, flexShrink: 0 },
  seg: { all: 'unset', cursor: 'pointer', padding: '3px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600, color: S.muted, '&:hover, &:focus-visible': { color: S.fg } },
  segOn: { background: S.field, color: S.fg, boxShadow: '0 1px 2px rgba(23, 26, 32, 0.15)' },
  close: {
    all: 'unset', cursor: 'pointer', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, color: S.muted, flexShrink: 0,
    '& .material-icons': { fontSize: 18 },
    '&:hover, &:focus-visible': { background: S.chromeHover, color: S.fg }
  },
  body: { overflow: 'auto', padding: PAD, minHeight: 0 },
  stage: { position: 'relative', margin: '0 auto', overflow: 'hidden', borderRadius: 6, background: S.field, boxShadow: `0 0 0 1px ${S.line}`, transition: 'height 160ms ease' },
  frame: { position: 'absolute', top: 0, left: 0, border: 0, transformOrigin: '0 0', background: S.field },
  waiting: {
    position: 'absolute', inset: 0,
    background: `linear-gradient(90deg, ${S.field} 25%, ${S.chromeHover} 50%, ${S.field} 75%)`,
    backgroundSize: '200% 100%',
    animation: '$shimmer 1.2s ease-in-out infinite'
  },
  '@keyframes shimmer': { '0%': { backgroundPosition: '-200% 0' }, '100%': { backgroundPosition: '200% 0' } },
  bar: { position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: S.clay, opacity: 0.8 },
  blank: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '0 40px',
    textAlign: 'center',
    '& .material-icons': { fontSize: 26, color: S.muted, marginBottom: 4 }
  },
  blankTitle: { fontSize: 14, fontWeight: 600 },
  // Even lines, not a last word on its own.
  blankText: { fontSize: 12.5, lineHeight: 1.45, color: S.muted, maxWidth: 380, textWrap: 'balance' },
  foot: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px 14px', borderTop: `1px solid ${S.line}` },
  note: { fontSize: 12, color: S.muted, marginRight: 'auto' },
  add: {
    all: 'unset', boxSizing: 'border-box', cursor: 'pointer', padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, background: S.fg, color: S.field, whiteSpace: 'nowrap',
    '&:hover, &:focus-visible': { background: S.fgActive }
  }
});

/**
 * A live look at an Insert item before adding it: the section rendered by the
 * site's own renderer, at desktop or phone width, beside the Insert panel.
 * Switching items keeps the last preview up until the next one is ready.
 */
const SectionPeek: React.FC<Props> = ({ pageId, item, anchor, placement, onAdd, onClose, hidden = false }) => {
  const c = useStyles();
  const [device, setDevice] = useState<Device>('desktop');
  const [shown, setShown] = useState<Frame | null>(null);
  const [next, setNext] = useState<Frame | null>(null);
  const [pos, setPos] = useState({ left: 0, top: 0, maxHeight: 600 });
  const sheet = useRef<HTMLElement>(null);
  const [notchTop, setNotchTop] = useState<number | null>(null);
  const firstKey = useRef(item.key);
  const shownRef = useRef<HTMLIFrameElement>(null);
  const nextRef = useRef<HTMLIFrameElement>(null);
  // Each device gets its own render, loaded at that width like a real phone or desktop loads
  // the page: shrinking a desktop render keeps whatever its scripts and layout worked out at
  // 1280px (sections came out shifted and cut off at 390px).
  const src = item.blank ? null : `${previewSrc(pageId, item)}&device=${device}`;

  // Beside the Insert panel, level with the row it shows (kept inside the window), with the
  // notch pointing at that row. Re-placed when the item, the preview's height, the list's
  // scroll or the window changes.
  useLayoutEffect(() => {
    const place = () => {
      const el = sheet.current;
      if (!anchor || !el) return;
      const a = anchor.getBoundingClientRect();
      const minTop = Math.max(8, a.top);
      const maxHeight = window.innerHeight - minTop - 12;
      const left = Math.min(a.right + 12, window.innerWidth - WIDTH - 12);
      const rowEl = anchor.querySelector(`[data-row-key="${item.key.replace(/["\\]/g, '\\$&')}"]`);
      const r = rowEl ? rowEl.getBoundingClientRect() : null;
      const mid = r ? r.top + r.height / 2 : null;
      const visible = mid !== null && mid >= a.top && mid <= a.bottom;
      const height = Math.min(el.offsetHeight, maxHeight);
      // The header lines up with the row; near the bottom of the window the sheet stops and the notch moves down instead.
      const top = visible && mid !== null
        ? Math.min(Math.max(mid - HEADER_MID, minTop), Math.max(minTop, window.innerHeight - height - 12))
        : minTop;
      setPos(prev => (prev.left === left && prev.top === top && prev.maxHeight === maxHeight ? prev : { left, top, maxHeight }));
      const notch = visible && mid !== null ? mid - top : null;
      setNotchTop(notch !== null && notch >= 14 && notch <= height - 14 ? notch : null);
    };
    place();
    anchor && anchor.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    const ro = RO && sheet.current ? new RO(place) : null;
    if (ro && sheet.current) ro.observe(sheet.current);
    return () => {
      anchor && anchor.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      if (ro) ro.disconnect();
    };
  }, [anchor, item.key]);

  // Opened on a different item than the one loaded out of sight: don't show that other section meanwhile.
  const wasHidden = useRef(hidden);
  useEffect(() => {
    if (wasHidden.current && !hidden && shown && shown.src !== src) setShown(null);
    wasHidden.current = hidden;
  }, [hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new item or device: load it next to the one on show. Nothing to load: drop what was on show, so the next
  // item doesn't start from a section other than the one before it.
  useEffect(() => {
    if (src === null) { setShown(null); setNext(null); return; }
    if (shown && shown.src === src) { setNext(null); return; }
    setNext({ src, height: null, device });
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  // The rendered section reports its height; that's when it's ready to show. Or that it's done and still has
  // nothing to show, or that there's no preview (and why): a placeholder is shown at once.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { gxSectionPreview?: number; height?: number; done?: boolean; error?: string } | null;
      if (!data || !data.gxSectionPreview) return;
      let patch: Partial<Frame> | null = null;
      if (typeof data.error === 'string') patch = { error: data.error.slice(0, 300) };
      // A page reports 0 before its scripts have drawn the section: not ready yet, unless it says it's done.
      else if (typeof data.height === 'number' && data.height >= 8) patch = { height: Math.min(data.height, 20000), empty: false };
      else if (data.done) patch = { empty: true };
      if (!patch) return;
      const p = patch;
      if (nextRef.current && e.source === nextRef.current.contentWindow) {
        setNext(n => (n ? { ...n, ...p } : n));
      } else if (shownRef.current && e.source === shownRef.current.contentWindow) {
        setShown(s => (s ? { ...s, ...p } : s));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => {
    if (!next) return undefined;
    if (next.height !== null || next.empty || next.error !== undefined) { setShown(next); setNext(null); return undefined; }
    // No height means it didn't render a section (an error page, say): show what loaded.
    const t = window.setTimeout(() => { setShown(next); setNext(null); }, 8000);
    return () => window.clearTimeout(t);
  }, [next]);

  // Esc, or a click outside the peek and the Insert panel, closes it (once it's on show).
  useEffect(() => {
    if (hidden) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (sheet.current && sheet.current.contains(target)) return;
      if (anchor && anchor.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey, true); document.removeEventListener('mousedown', onDown); };
  }, [anchor, onClose, hidden]);

  const inner = WIDTH - PAD * 2 - 2;
  const scaleOf = (dev: Device) => Math.min(1, inner / DEVICES[dev].width);
  // In place of a preview: the item has none, or the render on show had nothing to show (or failed). While the
  // next item loads after a placeholder, the stage waits (a placeholder isn't about the item now named above).
  const shownBlank = shown ? (shown.error !== undefined ? failed(shown.error) : shown.empty ? EMPTY : null) : null;
  const blank = item.blank || (shownBlank && !next ? shownBlank : null);
  const waiting = !blank && (!shown || Boolean(shownBlank));
  // The stage keeps the size of the render on show until the next one is ready.
  const stageDevice: Device = item.blank ? 'desktop' : shown ? shown.device : device;
  const sd = DEVICES[stageDevice];
  const visible = (h: number | null) => Math.round(Math.min(h === null ? sd.height : h, sd.height) * scaleOf(stageDevice));
  const frameStyle = (f: Frame, loading: boolean): React.CSSProperties => {
    const fd = DEVICES[f.device];
    const scale = scaleOf(f.device);
    // left: 0 — the stage is already exactly the frame's (scaled) width and centred. Centring the
    // frame again within the full panel width pushed the phone frame 88px right and cut it off.
    // Hidden while it loads; otherwise it inherits from the sheet. Never 'visible': a child set to visible
    // shows even when the sheet is hidden, so a closed preview's section stayed on the page without its frame.
    return { width: fd.width, height: fd.height, transform: `scale(${scale})`, left: 0, visibility: loading ? 'hidden' : undefined };
  };

  const frame = (f: Frame, ref: React.RefObject<HTMLIFrameElement>, loading: boolean) => (
    <iframe
      key={f.src}
      ref={ref}
      title={loading ? 'Loading preview' : `Preview of ${item.title}`}
      src={f.src}
      // Scripts run so the section looks right, but it can't reach the editor.
      sandbox="allow-scripts"
      className={c.frame}
      style={frameStyle(f, loading)}
    />
  );

  return ReactDOM.createPortal(
    <aside
      ref={sheet}
      className={c.sheet}
      style={{ left: pos.left, top: pos.top, maxHeight: pos.maxHeight, ...(hidden ? { visibility: 'hidden', pointerEvents: 'none', animation: 'none' } : {}) }}
      role={hidden ? undefined : 'dialog'}
      aria-label={hidden ? undefined : `Preview of ${item.title}`}
      aria-hidden={hidden || undefined}
      data-testid={hidden ? 'section-peek-preload' : 'section-peek'}
      data-item={item.key}
    >
      {notchTop !== null && <span className={c.notch} style={{ top: notchTop }} data-testid="peek-notch" aria-hidden />}
      <div className={c.head}>
        <div className={c.titles}>
          <div key={item.key} className={`${c.title} ${item.key !== firstKey.current ? c.flash : ''}`} data-testid="peek-title">{item.title}</div>
          <div className={c.meta}>{item.meta}</div>
        </div>
        {!item.blank && (
          <div className={c.segment} role="group" aria-label="Device">
            {(Object.keys(DEVICES) as Device[]).map(key => (
              <button key={key} type="button" className={`${c.seg} ${device === key ? c.segOn : ''}`} aria-pressed={device === key} onClick={() => setDevice(key)}>
                {DEVICES[key].label}
              </button>
            ))}
          </div>
        )}
        <button type="button" className={c.close} onClick={onClose} aria-label="Close preview"><i className="material-icons">close</i></button>
      </div>
      <div className={c.body}>
        <div
          className={c.stage}
          style={{ width: Math.min(inner, Math.round(sd.width * scaleOf(stageDevice))), height: blank ? BLANK_HEIGHT : visible(shown && !shownBlank ? shown.height : null) }}
          data-ready={Boolean(item.blank || (shown && !next))}
          data-device={stageDevice}
          data-blank={blank ? (item.blank ? 'none' : shown && shown.error !== undefined ? 'error' : 'empty') : undefined}
        >
          {waiting && <div className={c.waiting} />}
          {/* An empty render stays loaded out of sight: if it draws something after all, it's shown. */}
          {shown && frame(shown, shownRef, Boolean(shownBlank))}
          {next && frame(next, nextRef, true)}
          {shown && next && !shownBlank && <div className={c.bar} />}
          {blank && (
            <div className={c.blank} data-testid="peek-blank">
              <i className="material-icons" aria-hidden>{blank.icon}</i>
              <div className={c.blankTitle}>{blank.title}</div>
              <div className={c.blankText}>{blank.text}</div>
            </div>
          )}
        </div>
      </div>
      <div className={c.foot}>
        <span className={c.note}>
          {!blank && (item.kind === 'widget' ? 'Shown with its default content.' : 'As the site renders it.')}
          {/* A few widgets pick their phone version from the browser, not the width: they need a real phone. */}
          {!blank && device === 'mobile' && ' At phone width; a few sections only switch to their phone version on a real phone.'}
        </span>
        <button type="button" className={c.add} onClick={onAdd} data-testid="peek-add">Add {placement}</button>
      </div>
    </aside>,
    document.body
  );
};

export default SectionPeek;
